import React from "react";
import { Alert, Button, Checkbox, Collapse, Input, Select, Tag, Tooltip } from "antd";
import type { ChannelT } from "../../../flatbuffers/generated/archive/channel";
import { ChannelMode } from "../../../flatbuffers/generated/archive/channel-mode";
import { ChannelType } from "../../../flatbuffers/generated/archive/channel-type";
import { SessionConfigT } from "../../../flatbuffers/generated/session/session-config";
import { SessionCredentialsT } from "../../../flatbuffers/generated/session/session-credentials";
import { SessionProtocol } from "../../../flatbuffers/generated/session/session-protocol";
import { SshOptionsT } from "../../../flatbuffers/generated/session/ssh-options";
import { TelnetOptionsT } from "../../../flatbuffers/generated/session/telnet-options";
import { coerceFlatBufferText } from "../../../utils/bankFloorOpenings";

const { Panel } = Collapse;

/**
 * The name each session protocol has in the dropdown. Keyed by the generated
 * enum, so a protocol added to the schema stops this file compiling until it
 * is named here.
 */
const SESSION_PROTOCOL_LABELS: Record<SessionProtocol, string> = {
  [SessionProtocol.None]: "None",
  [SessionProtocol.Telnet]: "Telnet",
  [SessionProtocol.Ssh]: "SSH",
};

const SESSION_PROTOCOL_OPTIONS = Object.entries(SESSION_PROTOCOL_LABELS).map(([value, label]) => ({
  label,
  value: Number(value) as SessionProtocol,
}));

/**
 * An OpenSSH host key fingerprint as `ssh-keygen -lf` prints it: `SHA256:` and
 * the unpadded base64 of a 32-byte digest, which is 43 characters. LiftCloud
 * refuses any other pin, even beside "accept any host key".
 */
export const SSH_FINGERPRINT_PATTERN = /^SHA256:[A-Za-z0-9+/]{43}$/;

/** Shown when an SSH session neither pins a host key nor accepts any. */
export const SESSION_HOST_KEY_REQUIRED =
  "An SSH session needs the server's host key fingerprint, or Accept any host key ticked.";

/** Shown when the host key fingerprint is not in the form ssh-keygen prints. */
export const SESSION_HOST_KEY_FORMAT =
  "The host key fingerprint must be SHA256: followed by 43 base64 characters, as ssh-keygen -lf prints it.";

/** Shown when a password or private key would be stored without a username. */
export const SESSION_USERNAME_REQUIRED =
  "A session username is required while a session password or private key is set.";

/**
 * What the session section holds while a channel is created or edited.
 *
 * The settings and the secrets are separate generated tables on purpose:
 * `config` is all the wizard draft may persist, and nothing in it can hold a
 * secret. `credentials` is write-only — never filled from a response and never
 * persisted — so a stored secret is shown only through the flag LiftCloud
 * reports for it.
 */
export interface ChannelSessionState {
  /** Protocol, username and options being edited; both option tables are present. */
  config: SessionConfigT;
  /** The session as loaded, normalized; protocol None for a new channel. */
  loaded: SessionConfigT;
  /** Secrets typed in this edit. */
  credentials: SessionCredentialsT;
  /** LiftCloud reports a stored password (`session_password_configured`). */
  passwordStored: boolean;
  /** LiftCloud reports a stored private key (`session_private_key_configured`). */
  privateKeyStored: boolean;
  /** The operator asked to erase the stored password on save. */
  passwordClear: boolean;
  /** The operator asked to erase the stored private key and its passphrase on save. */
  privateKeyClear: boolean;
}

/** The four request keys the session section writes, typed by the generated ChannelT. */
export type SessionRequestFields = Pick<
  ChannelT,
  "session" | "sessionCredentials" | "sessionPasswordClear" | "sessionPrivateKeyClear"
>;

/** A FlatBuffer string field as text, "" when it is absent. */
const text = (value: unknown): string => coerceFlatBufferText(value) ?? "";

/** A string field with "" read as absent, which is what "" means to LiftCloud and VTS. */
function orNull(value: unknown): string | null {
  const t = text(value);
  return t === "" ? null : t;
}

/** A plain-object view of `value`, or null when it is not an object. */
function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

/** `value` when it is a member of the generated enum, otherwise None. */
function protocolOf(value: unknown): SessionProtocol {
  return typeof value === "number" && value in SESSION_PROTOCOL_LABELS
    ? (value as SessionProtocol)
    : SessionProtocol.None;
}

/**
 * Whether a channel may carry a session at all. LiftCloud refuses one on
 * anything but a SINGLE-mode channel whose connection type is TCP: a host-key
 * pin and a login belong to one host, and the endpoints of the other modes are
 * different hosts. The panel and the request body both ask this one question.
 */
export function sessionApplies(connectionType: ChannelType, mode: ChannelMode): boolean {
  return connectionType === ChannelType.TCP && mode === ChannelMode.SINGLE;
}

/** `config` with both option tables present, so every input has a field to bind to. */
function editable(config: SessionConfigT): SessionConfigT {
  const out = config.clone();
  out.telnet = out.telnet ?? new TelnetOptionsT();
  out.ssh = out.ssh ?? new SshOptionsT();
  return out;
}

/** The telnet overrides as stored: null when every field keeps its default. */
function normalizedTelnet(telnet: TelnetOptionsT | null): TelnetOptionsT | null {
  if (telnet === null) return null;
  const out = new TelnetOptionsT();
  out.usernamePrompt = orNull(telnet.usernamePrompt);
  out.passwordPrompt = orNull(telnet.passwordPrompt);
  out.readyMarker = orNull(telnet.readyMarker);
  out.failureMarker = orNull(telnet.failureMarker);
  return out.equals(new TelnetOptionsT()) ? null : out;
}

/** The SSH options as stored: the fingerprint trimmed, null when nothing is set. */
function normalizedSsh(ssh: SshOptionsT | null): SshOptionsT | null {
  if (ssh === null) return null;
  const out = new SshOptionsT();
  out.hostKeyFingerprint = orNull(text(ssh.hostKeyFingerprint).trim());
  out.acceptAnyHostKey = ssh.acceptAnyHostKey;
  out.command = orNull(ssh.command);
  out.requestPty = ssh.requestPty;
  return out.equals(new SshOptionsT()) ? null : out;
}

/**
 * The session as LiftCloud would store it: nothing but the protocol under None,
 * only the option table the protocol uses, "" read as absent, and the
 * fingerprint trimmed. Two settings that store the same compare equal.
 */
export function normalizedSessionConfig(config: SessionConfigT): SessionConfigT {
  const out = new SessionConfigT();
  out.protocol = config.protocol;
  if (config.protocol === SessionProtocol.None) return out;
  out.username = orNull(config.username);
  if (config.protocol === SessionProtocol.Telnet) out.telnet = normalizedTelnet(config.telnet);
  if (config.protocol === SessionProtocol.Ssh) out.ssh = normalizedSsh(config.ssh);
  return out;
}

/**
 * Rebuilds a channel's session settings from a response or a draft. The value
 * is a generated SessionConfigT, a structuredClone of one, or its JSON, so it is
 * read field by field; anything missing reads as protocol None and empty text.
 */
export function sessionConfigFrom(value: unknown): SessionConfigT {
  const raw = record(value);
  const telnetRaw = record(raw?.telnet);
  const sshRaw = record(raw?.ssh);
  const telnet = new TelnetOptionsT();
  telnet.usernamePrompt = text(telnetRaw?.usernamePrompt);
  telnet.passwordPrompt = text(telnetRaw?.passwordPrompt);
  telnet.readyMarker = text(telnetRaw?.readyMarker);
  telnet.failureMarker = text(telnetRaw?.failureMarker);
  const ssh = new SshOptionsT();
  ssh.hostKeyFingerprint = text(sshRaw?.hostKeyFingerprint);
  ssh.acceptAnyHostKey = sshRaw?.acceptAnyHostKey === true;
  ssh.command = text(sshRaw?.command);
  ssh.requestPty = sshRaw?.requestPty === true;
  const config = new SessionConfigT();
  config.protocol = protocolOf(raw?.protocol);
  config.username = text(raw?.username);
  config.telnet = telnet;
  config.ssh = ssh;
  return config;
}

/** The session section of a channel being created: protocol None unless a draft restores more. */
export function newChannelSession(
  config: SessionConfigT = new SessionConfigT(),
): ChannelSessionState {
  return {
    config: editable(config),
    loaded: new SessionConfigT(),
    credentials: new SessionCredentialsT(),
    passwordStored: false,
    privateKeyStored: false,
    passwordClear: false,
    privateKeyClear: false,
  };
}

/**
 * The session section of a channel loaded for editing: the settings from the
 * response's `session` table (absent under protocol None) and the stored-secret
 * flags from `sessionPasswordConfigured` / `sessionPrivateKeyConfigured`. No
 * secret is read — no response carries one — so every secret input starts empty.
 */
export function sessionStateFromChannel(channel: unknown): ChannelSessionState {
  const raw = record(channel);
  const config = sessionConfigFrom(raw?.session);
  return {
    ...newChannelSession(config),
    loaded: normalizedSessionConfig(config),
    passwordStored: raw?.sessionPasswordConfigured === true,
    privateKeyStored: raw?.sessionPrivateKeyConfigured === true,
  };
}

/** Whether a password or private key will be stored once the save lands. */
function secretAfterSave(state: ChannelSessionState): boolean {
  const typedPassword = text(state.credentials.password) !== "" && !state.passwordClear;
  const typedKey =
    state.config.protocol === SessionProtocol.Ssh &&
    text(state.credentials.privateKey) !== "" &&
    !state.privateKeyClear;
  return (
    typedPassword ||
    typedKey ||
    (state.passwordStored && !state.passwordClear) ||
    (state.privateKeyStored && !state.privateKeyClear)
  );
}

/**
 * The first session rule the form breaks, as the message the save button
 * shows, or null. They are LiftCloud's own rules, checked here so the operator
 * is told which field to fix instead of receiving a 400.
 */
export function sessionProblem(state: ChannelSessionState, applies: boolean): string | null {
  const { config } = state;
  if (!applies || config.protocol === SessionProtocol.None) return null;
  if (config.protocol === SessionProtocol.Ssh) {
    const fingerprint = text(config.ssh?.hostKeyFingerprint).trim();
    if (fingerprint !== "" && !SSH_FINGERPRINT_PATTERN.test(fingerprint)) {
      return SESSION_HOST_KEY_FORMAT;
    }
    if (fingerprint === "" && config.ssh?.acceptAnyHostKey !== true) {
      return SESSION_HOST_KEY_REQUIRED;
    }
  }
  if (secretAfterSave(state) && text(config.username) === "") return SESSION_USERNAME_REQUIRED;
  return null;
}

/** The secrets typed in this edit that the protocol uses, or null when there are none. */
function typedCredentials(state: ChannelSessionState): SessionCredentialsT | null {
  const { protocol } = state.config;
  if (protocol === SessionProtocol.None) return null;
  const credentials = new SessionCredentialsT();
  credentials.password = state.passwordClear ? null : orNull(state.credentials.password);
  if (protocol === SessionProtocol.Ssh && !state.privateKeyClear) {
    credentials.privateKey = orNull(state.credentials.privateKey);
    credentials.privateKeyPassphrase =
      credentials.privateKey === null ? null : orNull(state.credentials.privateKeyPassphrase);
  }
  return credentials.password === null && credentials.privateKey === null ? null : credentials;
}

/**
 * What a save sends for the session section.
 *
 * `session` goes only when it differs from what was loaded: LiftCloud keeps the
 * stored session when the table is absent, so an edit that never touched the
 * session — or a response that did not report one — cannot erase it. A channel
 * that no longer qualifies (not TCP, or not SINGLE) sends protocol None when it
 * had a session, because LiftCloud refuses to keep one there. Secrets go only
 * when typed, and only under a protocol that uses them: the password under
 * telnet or SSH, the private key under SSH, its passphrase only beside a key.
 * The clear flags go as the operator set them.
 */
export function sessionRequestFields(
  state: ChannelSessionState,
  applies: boolean,
): SessionRequestFields {
  const target = applies ? normalizedSessionConfig(state.config) : new SessionConfigT();
  const changed = !target.equals(normalizedSessionConfig(state.loaded));
  return {
    session: changed ? target : null,
    sessionCredentials: applies ? typedCredentials(state) : null,
    sessionPasswordClear: state.passwordClear,
    sessionPrivateKeyClear: state.privateKeyClear,
  };
}

type TelnetField = "usernamePrompt" | "passwordPrompt" | "readyMarker" | "failureMarker";
type SshPatch = Partial<
  Pick<SshOptionsT, "hostKeyFingerprint" | "acceptAnyHostKey" | "command" | "requestPty">
>;
type CredentialsPatch = Partial<
  Pick<SessionCredentialsT, "password" | "privateKey" | "privateKeyPassphrase">
>;

function withConfig(
  state: ChannelSessionState,
  patch: Partial<Pick<SessionConfigT, "protocol" | "username">>,
): ChannelSessionState {
  const config = editable(state.config);
  Object.assign(config, patch);
  return { ...state, config };
}

function withTelnet(
  state: ChannelSessionState,
  field: TelnetField,
  value: string,
): ChannelSessionState {
  const config = editable(state.config);
  const telnet = config.telnet ?? new TelnetOptionsT();
  telnet[field] = value;
  config.telnet = telnet;
  return { ...state, config };
}

function withSsh(state: ChannelSessionState, patch: SshPatch): ChannelSessionState {
  const config = editable(state.config);
  const ssh = config.ssh ?? new SshOptionsT();
  Object.assign(ssh, patch);
  config.ssh = ssh;
  return { ...state, config };
}

function withCredentials(state: ChannelSessionState, patch: CredentialsPatch): ChannelSessionState {
  const credentials = state.credentials.clone();
  Object.assign(credentials, patch);
  return { ...state, credentials };
}

/** The password input's hint, which is the only place a stored password shows. */
function passwordPlaceholder(state: ChannelSessionState): string {
  if (state.passwordClear) return "The stored password is cleared on save";
  if (state.passwordStored) return "Stored — type a new one to replace it";
  return "Not set";
}

/** The private key input's hint, which is the only place a stored key shows. */
function privateKeyPlaceholder(state: ChannelSessionState): string {
  if (state.privateKeyClear) return "The stored key and its passphrase are cleared on save";
  if (state.privateKeyStored) return "Stored — paste a new key to replace it";
  return "-----BEGIN OPENSSH PRIVATE KEY----- (Ed25519 or ECDSA)";
}

interface StoredSecretTagProps {
  stored: boolean;
  clearing: boolean;
  testId: string;
}

/** "Stored" while LiftCloud holds the secret, "Cleared on save" while a Clear is pending. */
function StoredSecretTag({ stored, clearing, testId }: StoredSecretTagProps) {
  if (clearing) {
    return (
      <Tag color="orange" data-testid={testId} style={{ marginLeft: 8 }}>
        Cleared on save
      </Tag>
    );
  }
  if (stored) {
    return (
      <Tag color="green" data-testid={testId} style={{ marginLeft: 8 }}>
        Stored
      </Tag>
    );
  }
  return null;
}

interface ChannelSessionEditorProps {
  /** The channel's connection type, as the generated enum the request carries. */
  connectionType: ChannelType;
  /** The channel's mode; a session needs SINGLE. */
  mode: ChannelMode;
  value: ChannelSessionState;
  onChange: (next: ChannelSessionState) => void;
}

/**
 * The channel's session protocol (telnet or SSH), its login and its options.
 * Rendered only for a SINGLE-mode TCP channel. Every secret input is
 * write-only: it starts empty, a stored secret shows as a "Stored" tag, and
 * Clear erases it on save.
 */
export default function ChannelSessionEditor({
  connectionType,
  mode,
  value,
  onChange,
}: ChannelSessionEditorProps) {
  if (!sessionApplies(connectionType, mode)) return null;
  const { config, credentials } = value;
  const { protocol } = config;
  const fingerprint = text(config.ssh?.hostKeyFingerprint).trim();
  const fingerprintMalformed = fingerprint !== "" && !SSH_FINGERPRINT_PATTERN.test(fingerprint);
  const keyTyped = text(credentials.privateKey) !== "";

  return (
    <div data-testid="channel-session-editor">
      <div className="row">
        <div className="col-12 col-md-4 mt-3">
          <Tooltip title="A login layered on the TCP connection before VTS speaks the vendor protocol. None connects directly, as before.">
            <span className="generalStyles__inputLabel">Session Protocol</span>
          </Tooltip>
          <Select
            data-doc-label="Session Protocol"
            data-testid="channel-session-protocol"
            style={{ width: "100%", marginTop: 4 }}
            size="large"
            value={protocol}
            onChange={(next: SessionProtocol) => onChange(withConfig(value, { protocol: next }))}
            options={SESSION_PROTOCOL_OPTIONS}
          />
        </div>
        {protocol !== SessionProtocol.None && (
          <div className="col-12 col-md-4 mt-3">
            <span className="generalStyles__inputLabel">Session Username</span>
            <Input
              data-doc-label="Session Username"
              data-testid="channel-session-username"
              style={{ marginTop: 4 }}
              size="large"
              autoComplete="off"
              value={text(config.username)}
              placeholder="Empty: the device asks for no login"
              onChange={(e) => onChange(withConfig(value, { username: e.target.value }))}
            />
          </div>
        )}
        {protocol !== SessionProtocol.None && (
          <div className="col-12 col-md-4 mt-3">
            <span className="generalStyles__inputLabel">Session Password</span>
            <StoredSecretTag
              stored={value.passwordStored}
              clearing={value.passwordClear}
              testId="channel-session-password-status"
            />
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Input.Password
                data-doc-label="Session Password"
                data-testid="channel-session-password"
                size="large"
                autoComplete="new-password"
                value={text(credentials.password)}
                disabled={value.passwordClear}
                placeholder={passwordPlaceholder(value)}
                onChange={(e) => onChange(withCredentials(value, { password: e.target.value }))}
              />
              {value.passwordStored && (
                <Button
                  data-doc-label="Clear Session Password"
                  data-testid="channel-session-password-clear"
                  size="large"
                  onClick={() =>
                    onChange(
                      value.passwordClear
                        ? { ...value, passwordClear: false }
                        : { ...withCredentials(value, { password: null }), passwordClear: true },
                    )
                  }
                >
                  {value.passwordClear ? "Keep" : "Clear"}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {protocol === SessionProtocol.Ssh && (
        <>
          <div className="row">
            <div className="col-12 col-md-6 mt-3">
              <Tooltip title="The SSH server's host key fingerprint, as `ssh-keygen -lf <key>.pub` prints it. VTS refuses a server that presents any other key.">
                <span className="generalStyles__inputLabel">Host Key Fingerprint</span>
              </Tooltip>
              <Input
                data-doc-label="Host Key Fingerprint"
                data-testid="channel-session-host-key"
                style={{ marginTop: 4 }}
                size="large"
                autoComplete="off"
                status={fingerprintMalformed ? "error" : undefined}
                value={text(config.ssh?.hostKeyFingerprint)}
                placeholder="SHA256:…"
                onChange={(e) => onChange(withSsh(value, { hostKeyFingerprint: e.target.value }))}
              />
              {fingerprintMalformed && (
                <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                  Must be SHA256: followed by 43 base64 characters
                </span>
              )}
              <div style={{ marginTop: 8 }}>
                <Checkbox
                  data-doc-label="Accept any host key"
                  data-testid="channel-session-accept-any-host-key"
                  checked={config.ssh?.acceptAnyHostKey === true}
                  onChange={(e) => onChange(withSsh(value, { acceptAnyHostKey: e.target.checked }))}
                >
                  Accept any host key
                </Checkbox>
              </div>
            </div>
            <div className="col-12 col-md-6 mt-3">
              <Tooltip title="Run this command with SSH exec and carry the vendor protocol over it. Leave empty to open a shell instead.">
                <span className="generalStyles__inputLabel">SSH Command</span>
              </Tooltip>
              <Input
                data-doc-label="SSH Command"
                data-testid="channel-session-ssh-command"
                style={{ marginTop: 4 }}
                size="large"
                autoComplete="off"
                value={text(config.ssh?.command)}
                placeholder="Empty: open a shell"
                onChange={(e) => onChange(withSsh(value, { command: e.target.value }))}
              />
              <div style={{ marginTop: 8 }}>
                <Checkbox
                  data-doc-label="Request a terminal (PTY)"
                  data-testid="channel-session-request-pty"
                  checked={config.ssh?.requestPty === true}
                  onChange={(e) => onChange(withSsh(value, { requestPty: e.target.checked }))}
                >
                  Request a terminal (PTY)
                </Checkbox>
              </div>
            </div>
          </div>
          {config.ssh?.acceptAnyHostKey === true && (
            <Alert
              data-testid="channel-session-accept-any-warning"
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="Any host key is accepted"
              description="The connection is encrypted but the server is not authenticated: anything answering at this address gets the login. VTS logs a warning with the fingerprint it sees on every connect — copy it into Host Key Fingerprint and untick this."
            />
          )}
          <div className="row">
            <div className="col-12 col-md-8 mt-3">
              <span className="generalStyles__inputLabel">Private Key</span>
              <StoredSecretTag
                stored={value.privateKeyStored}
                clearing={value.privateKeyClear}
                testId="channel-session-private-key-status"
              />
              <Input.TextArea
                data-testid="channel-session-private-key"
                style={{ marginTop: 4, fontFamily: "monospace" }}
                rows={4}
                autoComplete="off"
                spellCheck={false}
                value={text(credentials.privateKey)}
                disabled={value.privateKeyClear}
                placeholder={privateKeyPlaceholder(value)}
                onChange={(e) => onChange(withCredentials(value, { privateKey: e.target.value }))}
              />
              {value.privateKeyStored && (
                <Button
                  data-doc-label="Clear Private Key"
                  data-testid="channel-session-private-key-clear"
                  style={{ marginTop: 8 }}
                  onClick={() =>
                    onChange(
                      value.privateKeyClear
                        ? { ...value, privateKeyClear: false }
                        : {
                            ...withCredentials(value, {
                              privateKey: null,
                              privateKeyPassphrase: null,
                            }),
                            privateKeyClear: true,
                          },
                    )
                  }
                >
                  {value.privateKeyClear ? "Keep" : "Clear"}
                </Button>
              )}
            </div>
            <div className="col-12 col-md-4 mt-3">
              <span className="generalStyles__inputLabel">Key Passphrase</span>
              <Input.Password
                data-doc-label="Key Passphrase"
                data-testid="channel-session-key-passphrase"
                style={{ marginTop: 4 }}
                size="large"
                autoComplete="new-password"
                value={text(credentials.privateKeyPassphrase)}
                disabled={!keyTyped || value.privateKeyClear}
                placeholder={keyTyped ? "Only if the key is encrypted" : "Paste a key first"}
                onChange={(e) =>
                  onChange(withCredentials(value, { privateKeyPassphrase: e.target.value }))
                }
              />
            </div>
          </div>
        </>
      )}

      {protocol === SessionProtocol.Telnet && (
        <Collapse style={{ marginTop: 12 }}>
          <Panel header="Advanced: telnet login prompts" key="telnet">
            <p style={{ fontSize: 12, marginBottom: 0 }}>
              Each field is text VTS looks for, case-insensitively, in what the device sends. A
              value replaces the built-in ones; leave a field empty to keep them.
            </p>
            <div className="row">
              <div className="col-12 col-md-6 mt-3">
                <span className="generalStyles__inputLabel">Username Prompt</span>
                <Input
                  data-doc-label="Username Prompt"
                  data-testid="channel-session-telnet-username-prompt"
                  style={{ marginTop: 4 }}
                  autoComplete="off"
                  value={text(config.telnet?.usernamePrompt)}
                  placeholder="Built-in: user name:, username:, login:"
                  onChange={(e) => onChange(withTelnet(value, "usernamePrompt", e.target.value))}
                />
              </div>
              <div className="col-12 col-md-6 mt-3">
                <span className="generalStyles__inputLabel">Password Prompt</span>
                <Input
                  data-doc-label="Password Prompt"
                  data-testid="channel-session-telnet-password-prompt"
                  style={{ marginTop: 4 }}
                  autoComplete="off"
                  value={text(config.telnet?.passwordPrompt)}
                  placeholder="Built-in: password:"
                  onChange={(e) => onChange(withTelnet(value, "passwordPrompt", e.target.value))}
                />
              </div>
              <div className="col-12 col-md-6 mt-3">
                <span className="generalStyles__inputLabel">Ready Marker</span>
                <Input
                  data-doc-label="Ready Marker"
                  data-testid="channel-session-telnet-ready-marker"
                  style={{ marginTop: 4 }}
                  autoComplete="off"
                  value={text(config.telnet?.readyMarker)}
                  placeholder="Built-in: >, #, $, successfully, logged in, welcome"
                  onChange={(e) => onChange(withTelnet(value, "readyMarker", e.target.value))}
                />
              </div>
              <div className="col-12 col-md-6 mt-3">
                <span className="generalStyles__inputLabel">Failure Marker</span>
                <Input
                  data-doc-label="Failure Marker"
                  data-testid="channel-session-telnet-failure-marker"
                  style={{ marginTop: 4 }}
                  autoComplete="off"
                  value={text(config.telnet?.failureMarker)}
                  placeholder="Built-in: denied, incorrect, invalid, fail"
                  onChange={(e) => onChange(withTelnet(value, "failureMarker", e.target.value))}
                />
              </div>
            </div>
          </Panel>
        </Collapse>
      )}
    </div>
  );
}
